import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import axios from "axios";

const ProductMultiRegion = ({
  open,
  onClose,
  selectedRegions = [],
  onSave,
  data = [],
}) => {
  const [regionSettings, setRegionSettings] = useState([]);
  const [branchListVisibleRegions, setBranchListVisibleRegions] = useState([]);
  const [regionBranches, setRegionBranches] = useState({});
  const [selectedBranches, setSelectedBranches] = useState({});

  useEffect(() => {
    const initialSettings = selectedRegions.map((region) => ({
      ...region,
      bestSeller: data?.some(
        (item) => item?.region_id === region?.value && item?.best_selling
      ),
      newProduct: data?.some(
        (item) => item?.region_id === region?.value && item?.new_product
      ),
      pickUp: data.some(
        (item) => item?.region_id === region?.value && item?.pick_up
      ),
      doorDelivery: data.some(
        (item) => item?.region_id === region?.value && item?.door_step_delivery
      ),
    }));
    setRegionSettings(initialSettings);

    const visibleRegions = initialSettings
      .filter((r) => r.pickUp)
      .map((r) => r.value);
    setBranchListVisibleRegions(visibleRegions);

    const initialSelectedBranches = {};
    visibleRegions.forEach((regionId) => {
      const regionData = data.find((item) => item.region_id === regionId);
      if (regionData && regionData.shop_id) {
        const shopIds =
          typeof regionData.shop_id === "string"
            ? regionData.shop_id.split(",").map((id) => id.trim())
            : Array.isArray(regionData.shop_id)
            ? regionData.shop_id.map(String)
            : [];
        initialSelectedBranches[regionId] = new Set(shopIds);
      } else {
        initialSelectedBranches[regionId] = new Set();
      }
    });
    setSelectedBranches(initialSelectedBranches);

    if (visibleRegions.length > 0) {
      fetchBranchesForRegions(visibleRegions);
    }

    onSave && onSave(productTags(initialSettings, initialSelectedBranches));
  }, [JSON.stringify(selectedRegions), JSON.stringify(data)]);

  const fetchBranches = async (regionId) => {
    try {
      const response = await axios.get(
        `/getAllShopDetailsByRegionId/${regionId}`
      );
      const data = response.data.data;
      setRegionBranches((prev) => ({
        ...prev,
        [regionId]: data,
      }));
      setSelectedBranches((prev) => ({
        ...prev,
        [regionId]: new Set(),
      }));
    } catch (error) {
      console.error(`Failed to fetch branches for region ${regionId}`, error);
    }
  };

  const fetchBranchesForRegions = async (regionIds) => {
    try {
      const results = await Promise.all(
        regionIds.map(async (regionId) => {
          const response = await axios.get(
            `/getAllShopDetailsByRegionId/${regionId}`
          );
          const data = response.data.data;
          return { regionId, data };
        })
      );

      const branchesByRegion = {};
      results.forEach(({ regionId, data }) => {
        branchesByRegion[regionId] = data;
      });

      setRegionBranches((prev) => ({ ...prev, ...branchesByRegion }));

      setSelectedBranches((prev) => {
        const updated = { ...prev };
        regionIds.forEach((id) => {
          if (!updated[id]) {
            updated[id] = new Set();
          }
        });
        return updated;
      });
    } catch (error) {
      console.error("Error fetching branches for regions:", error);
    }
  };

  const productTags = (regionSettingsData, selectedBranchesData) =>
    regionSettingsData?.map((region) => ({
      region_id: region.value,
      best_selling: region.bestSeller ? 1 : 0,
      new_product: region.newProduct ? 1 : 0,
      pick_up: branchListVisibleRegions.includes(region.value) ? 1 : 0,
      door_delivery: region.doorDelivery ? 1 : 0,
      shop_id: Array.from(selectedBranchesData[region.value] || []),
    }));

  const handleCheckboxChange = (index, field) => {
    setRegionSettings((prev) =>
      prev.map((region, i) =>
        i === index ? { ...region, [field]: !region[field] } : region
      )
    );
  };

  const handlePickUpToggle = (regionValue) => {
    setBranchListVisibleRegions((prev) => {
      const isVisible = prev.includes(regionValue);
      let newList;
      if (isVisible) {
        newList = prev.filter((v) => v !== regionValue);
        setRegionBranches((prevBranches) => {
          const copy = { ...prevBranches };
          delete copy[regionValue];
          return copy;
        });
        setSelectedBranches((prevSelected) => {
          const copy = { ...prevSelected };
          delete copy[regionValue];
          return copy;
        });
      } else {
        newList = [...prev, regionValue];
        fetchBranches(regionValue);
      }
      return newList;
    });

    setRegionSettings((prev) =>
      prev.map((region) =>
        region.value === regionValue
          ? { ...region, pickUp: !region.pickUp }
          : region
      )
    );
  };

  const handleBranchToggle = (regionId, branchId) => {
    setSelectedBranches((prev) => {
      const selectedSet = new Set(prev[regionId] || []);
      if (selectedSet.has(branchId)) {
        selectedSet.delete(branchId);
      } else {
        selectedSet.add(branchId);
      }
      return {
        ...prev,
        [regionId]: selectedSet,
      };
    });
  };
  const handleSelectAllBranches = (regionId, branches) => {
    setSelectedBranches((prev) => {
      const newSet = new Set();
      const allSelected = branches.every((branch) =>
        prev[regionId]?.has(String(branch.shop_details_id))
      );

      // If all are selected, unselect all. Else, select all.
      if (!allSelected) {
        branches.forEach((branch) => {
          newSet.add(String(branch.shop_details_id));
        });
      }

      return {
        ...prev,
        [regionId]: allSelected ? new Set() : newSet,
      };
    });
  };

  useEffect(() => {
    onSave && onSave(productTags(regionSettings, selectedBranches));
  }, [regionSettings, branchListVisibleRegions, selectedBranches]);

  const columns = [
    {
      name: "id",
      label: "S.No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{dataIndex + 1}</p>,
        filter: false,
      },
    },
    {
      name: "label",
      label: "City",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "newProduct",
      label: "New Product",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => (
          <Checkbox
            checked={regionSettings[dataIndex]?.newProduct || false}
            onChange={() => handleCheckboxChange(dataIndex, "newProduct")}
          />
        ),
      },
    },
    {
      name: "bestSeller",
      label: "Best Seller",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => (
          <Checkbox
            checked={regionSettings[dataIndex]?.bestSeller || false}
            onChange={() => handleCheckboxChange(dataIndex, "bestSeller")}
          />
        ),
      },
    },
    {
      name: "pickUp",
      label: "Pick Up",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const regionValue = regionSettings[dataIndex]?.value;
          return (
            <Checkbox
              checked={branchListVisibleRegions.includes(regionValue)}
              onChange={() => handlePickUpToggle(regionValue)}
              color="primary"
            />
          );
        },
      },
    },
    {
      name: "doorDelivery",
      label: "Door Delivery",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => (
          <Checkbox
            checked={regionSettings[dataIndex]?.doorDelivery || false}
            onChange={() => handleCheckboxChange(dataIndex, "doorDelivery")}
          />
        ),
      },
    },
  ];

  const options = {
    selectableRows: "none",
    download: false,
    print: false,
    search: false,
    filter: false,
    viewColumns: false,
    pagination: false,
    expandableRows: true,
    expandableRowsOnClick: true,
    renderExpandableRow: (rowData, rowMeta) => {
      const dataIndex = rowMeta.dataIndex;
      const region = regionSettings[dataIndex];
      const regionValue = region.value;
      const branches = regionBranches[regionValue] || [];

      const allSelected =
        branches.length > 0 &&
        branches.every((branch) =>
          selectedBranches[regionValue]?.has(String(branch.shop_details_id))
        );

      return (
        <tr>
          <td colSpan={columns.length + 1} style={{ padding: 0, margin: 0 }}>
            <Box
              sx={{
                width: "100%",
                overflowX: "auto",
                px: 3,
                py: 2,
                bgcolor: "#f5f5f5",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {region.label} Branches
                </Typography>
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={allSelected}
                    onChange={() =>
                      handleSelectAllBranches(regionValue, branches)
                    }
                    size="small"
                  />
                  <Typography variant="body2">Select All</Typography>
                </Box>
              </Box>

              {branches.length > 0 ? (
                <Grid container spacing={1} wrap="wrap">
                  {branches.map((branch) => (
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      md={3}
                      lg={2}
                      key={branch.shop_details_id}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <Checkbox
                          checked={
                            selectedBranches[regionValue]?.has(
                              String(branch.shop_details_id)
                            ) || false
                          }
                          onChange={() =>
                            handleBranchToggle(
                              regionValue,
                              String(branch.shop_details_id)
                            )
                          }
                          size="small"
                        />
                        <Typography
                          variant="body2"
                          title={branch.shop_code}
                          sx={{
                            ml: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {branch.shop_code}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography sx={{ px: 2 }}>
                  No branches found for {region?.label}.
                </Typography>
              )}
            </Box>
          </td>
        </tr>
      );
    },
  };

  const handleSave = () => {
    onSave && onSave(productTags(regionSettings, selectedBranches));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Product Tags</DialogTitle>
      <DialogContent dividers>
        {regionSettings.length > 0 ? (
          <MUIDataTable
            size="small"
            title=""
            data={regionSettings}
            columns={columns}
            options={options}
          />
        ) : (
          <Typography>No regions selected.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1, justifyContent: "flex-end", pr: 2, pb: 1 }}>
        <Button variant="contained" color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductMultiRegion;
